import JSZip from "jszip";
import Papa from "papaparse";
import proj4 from "proj4";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import FileDrop from "senf-atomic-design-system/src/components/core/fileDrop/FileDrop";
import { getFileTypeLabel } from "senf-shared/utils/helpers";
import { read } from "shapefile";
import { cn } from "../../../../../util/classNameConstructor";
import { useModals } from "../../../../molecules/modalStack/ModalProvider";
import Tabs from "../../../../molecules/tabs/Tabs";
import BimUploader, { type SavedAsset } from "./BimUploader";

// File type detection utilities - based on originally supported formats
const GIS_FILE_EXTENSIONS = [
  ".geojson",
  ".json",
  ".csv",
  ".shp",
  ".dbf",
  ".prj",
  ".shx", // Shapefile components
];

const BIM_FILE_EXTENSIONS = [
  ".ifc", // Primary BIM format
  ".dwg",
  ".dxf",
  ".rvt",
  ".rfa",
  ".skp",
  ".3ds",
  ".obj",
  ".fbx",
  ".dae",
];

// Analyze ZIP contents to determine if it's GIS or BIM
const analyzeZipContents = async (
  file: File,
): Promise<"gis" | "bim" | "unknown"> => {
  try {
    const zip = await JSZip.loadAsync(file);
    const filenames = Object.keys(zip.files);

    let gisFiles = 0;
    let bimFiles = 0;

    filenames.forEach((filename) => {
      if (zip.files[filename].dir) return; // Skip directories

      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));

      if (GIS_FILE_EXTENSIONS.includes(fileExtension)) {
        gisFiles++;
      } else if (BIM_FILE_EXTENSIONS.includes(fileExtension)) {
        bimFiles++;
      }
    });

    // Determine type based on file counts
    if (gisFiles > bimFiles) {
      return "gis";
    }
    if (bimFiles > gisFiles) {
      return "bim";
    }
    if (gisFiles > 0) {
      return "gis"; // Default to GIS if equal or unknown
    }

    return "unknown";
  } catch (error: unknown) {
    console.error("Error analyzing ZIP contents:", error);
    return "unknown";
  }
};

const detectFileType = (file: File): "gis" | "bim" | "unknown" => {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  if (GIS_FILE_EXTENSIONS.includes(fileExtension)) {
    return "gis";
  }

  if (BIM_FILE_EXTENSIONS.includes(fileExtension)) {
    return "bim";
  }

  // ZIP files need content analysis
  if (fileExtension === ".zip") {
    return "unknown"; // Will be analyzed separately
  }

  return "unknown";
};

// Helper function to count files in ZIP archive based on type
const countFilesInZip = async (
  zipFile: File,
  targetType: "gis" | "bim",
): Promise<number> => {
  try {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    const allowedExtensions =
      targetType === "gis"
        ? [".geojson", ".json", ".csv", ".shp", ".dbf", ".prj", ".shx"]
        : [
            ".ifc",
            ".dwg",
            ".dxf",
            ".rvt",
            ".rfa",
            ".skp",
            ".3ds",
            ".obj",
            ".fbx",
            ".dae",
          ];

    let count = 0;
    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      // Skip directories and hidden files
      if (
        zipEntry.dir ||
        filename.startsWith("__MACOSX/") ||
        filename.startsWith(".DS_Store")
      ) {
        continue;
      }

      // Check if file has allowed extension
      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));
      if (allowedExtensions.includes(fileExtension)) {
        count++;
      }
    }

    return count;
  } catch (error) {
    console.error(`❌ Failed to count files in ZIP ${zipFile.name}:`, error);
    return 1; // Fallback to 1 if counting fails
  }
};

// Extract files from ZIP archive based on type
const extractFilesFromZip = async (
  zipFile: File,
  targetType: "gis" | "bim",
): Promise<File[]> => {
  try {
    console.log(
      `📦 Extracting ${targetType.toUpperCase()} files from ZIP: ${zipFile.name}`,
    );
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    const extractedFiles: File[] = [];
    const allowedExtensions =
      targetType === "gis"
        ? [".geojson", ".json", ".csv", ".shp", ".dbf", ".prj", ".shx"]
        : [
            ".ifc",
            ".dwg",
            ".dxf",
            ".rvt",
            ".rfa",
            ".skp",
            ".3ds",
            ".obj",
            ".fbx",
            ".dae",
          ];

    // Process each file in the ZIP
    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      // Skip directories and hidden files
      if (
        zipEntry.dir ||
        filename.startsWith("__MACOSX/") ||
        filename.startsWith(".DS_Store")
      ) {
        continue;
      }

      // Check if file has allowed extension
      const fileExtension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));
      if (!allowedExtensions.includes(fileExtension)) {
        console.log(
          `⏭️ Skipping non-${targetType.toUpperCase()} file: ${filename}`,
        );
        continue;
      }

      try {
        // Extract file content
        const fileContent = await zipEntry.async("blob");
        const extractedFile = new File([fileContent], filename, {
          type: "application/octet-stream",
        });
        extractedFiles.push(extractedFile);
        console.log(`✅ Extracted: ${filename}`);
      } catch (error: unknown) {
        console.error(`❌ Failed to extract ${filename}:`, error);
      }
    }

    console.log(
      `📦 ZIP extraction complete: ${extractedFiles.length} ${targetType.toUpperCase()} files found`,
    );
    return extractedFiles;
  } catch (error: unknown) {
    console.error(`❌ Failed to extract ZIP file ${zipFile.name}:`, error);
    throw new Error(
      `Failed to extract ZIP file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
};

const categorizeFiles = async (
  files: File[],
  onProgressUpdate?: (extractedCount: number) => void,
) => {
  const gisFiles: File[] = [];
  const bimFiles: File[] = [];
  const unknownFiles: File[] = [];
  const zipFileNames: string[] = []; // Track original ZIP file names

  // First, count total extractable files for progress tracking
  let totalExtractableFiles = 0;
  for (const file of files) {
    const type = detectFileType(file);

    if (type === "unknown" && file.name.toLowerCase().endsWith(".zip")) {
      const zipType = await analyzeZipContents(file);
      if (zipType === "gis") {
        const count = await countFilesInZip(file, "gis");
        totalExtractableFiles += count;
      } else if (zipType === "bim") {
        const count = await countFilesInZip(file, "bim");
        totalExtractableFiles += count;
      } else {
        totalExtractableFiles += 1; // Unknown ZIP counts as 1
      }
    } else {
      totalExtractableFiles += 1; // Non-ZIP files count as 1
    }
  }

  // Update progress with total count
  if (onProgressUpdate) {
    onProgressUpdate(totalExtractableFiles);
    console.log(
      `📊 Will extract ${totalExtractableFiles} files from ${files.length} input files`,
    );
  }

  for (const file of files) {
    const type = detectFileType(file);

    if (type === "unknown" && file.name.toLowerCase().endsWith(".zip")) {
      // Track ZIP file name for later use
      const zipBaseName = file.name.replace(/\.zip$/i, "");

      // Analyze ZIP contents and extract if appropriate
      const zipType = await analyzeZipContents(file);
      if (zipType === "gis") {
        try {
          const extractedFiles = await extractFilesFromZip(file, "gis");
          gisFiles.push(...extractedFiles);
          if (extractedFiles.length > 0) {
            zipFileNames.push(zipBaseName);
          }
        } catch (error: unknown) {
          console.error("Failed to extract GIS ZIP:", error);
          gisFiles.push(file); // Fallback to original ZIP
        }
      } else if (zipType === "bim") {
        try {
          const extractedFiles = await extractFilesFromZip(file, "bim");
          bimFiles.push(...extractedFiles);
          if (extractedFiles.length > 0) {
            zipFileNames.push(zipBaseName);
          }
        } catch (error: unknown) {
          console.error("Failed to extract BIM ZIP:", error);
          bimFiles.push(file); // Fallback to original ZIP
        }
      } else {
        unknownFiles.push(file);
      }
    } else {
      switch (type) {
        case "gis":
          gisFiles.push(file);
          break;
        case "bim":
          bimFiles.push(file);
          break;
        default:
          unknownFiles.push(file);
          break;
      }
    }
  }

  return { gisFiles, bimFiles, unknownFiles, zipFileNames };
};

// Note: getLabel will be defined as useCallback inside the component

// Define the UTM projection string for your specific zone and the WGS84 projection string
const utmProjection =
  "+proj=utm +zone=32 +ellps=WGS84 +datum=WGS84 +units=m +no_defs"; // Example: Zone 32N
const wgs84Projection = "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs";

// Helper function to check if a value is a finite number
const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === "number" && Number.isFinite(value);
};

// Function to convert a single coordinate
const convertCoordinate = (coord, fromProjection, toProjection) => {
  if (isFiniteNumber(coord[0]) && isFiniteNumber(coord[1])) {
    return proj4(fromProjection, toProjection, coord);
  }
  console.error("Invalid coordinate:", coord);
  return null; // Or handle invalid coordinates as needed
};

// Function to convert coordinates in a GeoJSON object
const convertGeoJSONCoordinates = (geojson, fromProjection, toProjection) => {
  geojson.features = geojson.features.map((feature) => {
    const { coordinates } = feature.geometry;
    if (feature.geometry.type === "Point") {
      const convertedCoord = convertCoordinate(
        coordinates,
        fromProjection,
        toProjection,
      );
      if (convertedCoord) {
        feature.geometry.coordinates = convertedCoord;
      }
    } else if (
      feature.geometry.type === "MultiPoint" ||
      feature.geometry.type === "LineString"
    ) {
      feature.geometry.coordinates = coordinates
        .map((coord) => convertCoordinate(coord, fromProjection, toProjection))
        .filter((coord) => coord !== null);
    } else if (
      feature.geometry.type === "MultiLineString" ||
      feature.geometry.type === "Polygon"
    ) {
      feature.geometry.coordinates = coordinates
        .map((line) =>
          line.map((coord) =>
            convertCoordinate(coord, fromProjection, toProjection),
          ),
        )
        .map((line) => line.filter((coord) => coord !== null));
    } else if (feature.geometry.type === "MultiPolygon") {
      feature.geometry.coordinates = coordinates.map((polygon) =>
        polygon
          .map((line) =>
            line.map((coord) =>
              convertCoordinate(coord, fromProjection, toProjection),
            ),
          )
          .map((line) => line.filter((coord) => coord !== null)),
      );
    }
    return feature;
  });
  return geojson;
};

// Function to check if the coordinates seem to be in UTM format (simple heuristic)
const seemsUTM = (coord) => {
  // Check if the coordinate is likely in UTM format by its numeric range
  return Math.abs(coord[0]) > 180 || Math.abs(coord[1]) > 90;
};

// Function to find the first valid coordinate in a potentially complex GeoJSON structure
const findFirstValidCoordinate = (coordinates) => {
  let firstCoord = coordinates;
  while (Array.isArray(firstCoord[0])) {
    firstCoord = firstCoord[0];
    if (firstCoord.length === 0) {
      return null; // No valid coordinate found
    }
  }
  return firstCoord;
};

// Function to convert a GeoJSON object from UTM to WGS84 if necessary
const autoConvertGeoJSONFromUTMToWGS84 = (geojson, utmProjection) => {
  if (
    geojson.features.length > 0 &&
    geojson.features[0].geometry.coordinates.length > 0
  ) {
    // Find the first valid coordinate to check
    const firstFoundCoordinate = findFirstValidCoordinate(
      geojson.features[0].geometry.coordinates,
    );

    // If the first coordinate doesn't seem to be in UTM or is null, return the original geojson
    if (!firstFoundCoordinate || !seemsUTM(firstFoundCoordinate)) {
      return geojson;
    }
  }

  // Proceed with conversion if the first coordinate seems to be in UTM
  return convertGeoJSONCoordinates(geojson, utmProjection, wgs84Projection);
};

interface GeoJSONUploaderProps {
  onFileUpload: (data: any, fileType: string, index?: number) => void;
  text?: string;
  fileTypes?: string[];
  fileTypesText?: string;
  isButton?: boolean;
  initialStagedFiles?: File[];
  isExtracting?: boolean; // Whether ZIP extraction is happening (for loading state)
  parentExtractingFileCount?: number; // Number of files being extracted by parent
}

export const GeoJSONUploader = ({
  onFileUpload,
  text,
  fileTypes,
  fileTypesText,
  isButton = false,
  initialStagedFiles = [],
  isExtracting: parentIsExtracting = false,
  parentExtractingFileCount = 0,
  ...props
}: GeoJSONUploaderProps) => {
  const { t } = useTranslation();
  const { closeModal, openModal } = useModals();
  const [stagedFiles, setStagedFiles] = useState<File[]>(initialStagedFiles);
  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [localExtractingFileCount, setLocalExtractingFileCount] =
    useState<number>(0);

  // Define file types and fileTypeLabel for getLabel function

  // getLabel function exactly like in ActionBar
  const getLabel = useCallback(
    (n = 1) => {
      return t(getFileTypeLabel(n));
    },
    [t],
  );

  // Update staged files when initialStagedFiles changes
  useEffect(() => {
    if (initialStagedFiles.length > 0) {
      setStagedFiles(initialStagedFiles);
    }
  }, [initialStagedFiles]);

  const extractFromZip = async (file: File) => {
    try {
      const zip = await JSZip.loadAsync(file);

      // Filter out the .shp and .dbf files, ignoring macOS metadata files
      const shpFiles = zip.file(/^(?!__MACOSX\/).*\.shp$/i); // Ignore macOS metadata files
      const dbfFiles = zip.file(/^(?!__MACOSX\/).*\.dbf$/i); // Ignore macOS metadata files

      const pairs: Array<{
        shpFile: JSZip.JSZipObject;
        dbfFile: JSZip.JSZipObject;
      }> = [];

      // Map dbf files by name for quick access
      const dbfMap: Record<string, JSZip.JSZipObject> = dbfFiles.reduce(
        (map, file) => {
          if (file) {
            map[file.name.replace(".dbf", "")] = file;
          }
          return map;
        },
        {} as Record<string, JSZip.JSZipObject>,
      );

      // Iterate over shpFiles and find the corresponding dbf file
      shpFiles.forEach((shpFile) => {
        if (shpFile) {
          const baseName = shpFile.name.replace(".shp", "");
          if (dbfMap[baseName]) {
            pairs.push({ shpFile, dbfFile: dbfMap[baseName] });
          }
        }
      });

      // Error handling if no pairs are found
      if (pairs.length === 0) {
        alert("No matching .shp and .dbf file pairs found.");
        return null;
      }

      return pairs;
    } catch (error: unknown) {
      console.error("Error extracting files from zip:", error);
      alert("Error extracting files from zip.");
      return null; // Return null to indicate an error
    }
  };

  const shpToGeoJSON = async (fileContent) => {
    const pairs = await extractFromZip(fileContent);

    if (!pairs || pairs.length === 0) {
      return; // Exit early if there's an error
    }

    try {
      // Assuming pairs is an array, we can use a traditional for loop to get access to the index
      for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i];
        const shpArrayBuffer = await pair.shpFile.async("arraybuffer");
        const dbfArrayBuffer = await pair.dbfFile.async("arraybuffer");

        const geojson = await read(shpArrayBuffer, dbfArrayBuffer);

        const convertedGeoJSON = autoConvertGeoJSONFromUTMToWGS84(
          geojson,
          utmProjection,
        );

        // Now you can use the index `i` if needed
        onFileUpload(
          { data: convertedGeoJSON, title: pair.shpFile.name }, // Example usage of index
          "zip",
          i,
        );
      }
    } catch (error: unknown) {
      console.error(error);
      // Handle any errors
    }
  };

  const csvToGeoJSON = (csvData: string) => {
    const geojson = {
      type: "FeatureCollection" as const,
      features: [] as Array<{
        type: "Feature";
        properties: any;
        geometry: {
          type: "Point";
          coordinates: [number, number];
        };
      }>,
    };

    Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        results.data.forEach((row: any) => {
          const long =
            row?.Longitude ||
            row?.longitude ||
            row?.Long ||
            row?.long ||
            row?.Lon ||
            row?.lon;
          const lat = row?.Latitude || row?.latitude || row?.Lat || row.lat;

          const formattedLong = Number.parseFloat(long?.replace(",", "."));
          const formattedLat = Number.parseFloat(lat?.replace(",", "."));

          const feature = {
            type: "Feature" as const,
            properties: row,
            geometry: {
              type: "Point" as const,
              coordinates: [formattedLong, formattedLat] as [number, number],
            },
          };
          geojson.features.push(feature);
        });
      },
    });

    return geojson;
  };

  const convertToGeoJSON = async (
    fileContent: string | File,
    fileType: string,
  ) => {
    if (fileType === "geojson" || fileType === "json") {
      if (typeof fileContent !== "string") {
        throw new Error("Expected string content for JSON/GeoJSON files");
      }
      const geojson = JSON.parse(fileContent);
      const convertedGeoJSON = autoConvertGeoJSONFromUTMToWGS84(
        geojson,
        utmProjection,
      );

      return await convertedGeoJSON;
    }

    if (fileType === "csv") {
      if (typeof fileContent !== "string") {
        throw new Error("Expected string content for CSV files");
      }
      return csvToGeoJSON(fileContent);
    }

    if (fileType === "zip" || fileType === "shp") {
      if (!(fileContent instanceof File)) {
        throw new Error("Expected File object for ZIP/SHP files");
      }
      return shpToGeoJSON(fileContent);
    }

    throw new Error("Unsupported file format");
  };

  // Handle file staging (drag & drop) - with ZIP extraction for direct uploads
  const handleFileChange = async (files: FileList | File[]) => {
    const filesArray = Array.from(files);

    // First, validate files and reject unsupported formats
    const rejectedFiles: string[] = [];
    const validFiles: File[] = [];

    for (const file of filesArray) {
      const fileType = detectFileType(file);

      // Only allow GIS files and ZIP files
      if (fileType === "gis") {
        validFiles.push(file);
      } else if (file.name.toLowerCase().endsWith(".zip")) {
        // For ZIP files, check if they contain GIS data
        const zipType = await analyzeZipContents(file);
        if (zipType === "bim") {
          rejectedFiles.push(file.name);
        } else {
          // Allow GIS ZIPs and unknown ZIPs (could contain valid data)
          validFiles.push(file);
        }
      } else {
        // Reject BIM files and any other unsupported formats (images, PDFs, etc.)
        rejectedFiles.push(file.name);
      }
    }

    // Show error message if unsupported files were rejected
    if (rejectedFiles.length > 0) {
      const fileList =
        rejectedFiles.length > 3
          ? `${rejectedFiles.slice(0, 3).join(", ")} ${t("global:and")} ${rejectedFiles.length - 3} ${t("global:more")}`
          : rejectedFiles.join(", ");

      const title = t("errors:fileValidation.unsupportedFormat.title");
      const message =
        rejectedFiles.length === 1
          ? t("errors:fileValidation.unsupportedFormat.singleFile", {
              fileName: rejectedFiles[0],
            })
          : t("errors:fileValidation.unsupportedFormat.multipleFiles", {
              fileList,
            });

      openModal(null, {
        title,
        description: message,
        id: "file-validation-error",
        zIndex: 10001,
      });

      // If all files were rejected, return early
      if (validFiles.length === 0) {
        return;
      }
    }

    const hasZipFiles = validFiles.some((file) =>
      file.name.toLowerCase().endsWith(".zip"),
    );

    if (hasZipFiles) {
      setIsExtracting(true);

      // Count total extractable files first
      let totalExtractableFiles = 0;
      for (const file of validFiles) {
        if (file.name.toLowerCase().endsWith(".zip")) {
          const zipType = await analyzeZipContents(file);
          if (zipType === "gis") {
            const fileCount = await countFilesInZip(file, "gis");
            totalExtractableFiles += fileCount;
          } else {
            totalExtractableFiles += 1; // Non-GIS ZIP counts as 1
          }
        } else {
          totalExtractableFiles += 1; // Non-ZIP files count as 1
        }
      }

      setLocalExtractingFileCount(totalExtractableFiles);
      console.log(
        `📊 Will extract ${totalExtractableFiles} files from ${validFiles.length} input files`,
      );
    }

    try {
      const processedFiles: File[] = [];

      // Process files and extract ZIP archives if they contain GIS files
      for (const file of validFiles) {
        if (file.name.toLowerCase().endsWith(".zip")) {
          try {
            const zipType = await analyzeZipContents(file);
            if (zipType === "gis") {
              console.log(
                `📦 Processing GIS ZIP file in GeoJSONUploader: ${file.name}`,
              );
              const extractedFiles = await extractFilesFromZip(file, "gis");
              processedFiles.push(...extractedFiles);
            } else if (zipType === "unknown") {
              // If it's an unknown ZIP (could contain mixed files), add the ZIP file itself
              console.log(
                `📦 Processing unknown ZIP file in GeoJSONUploader: ${file.name}`,
              );
              processedFiles.push(file);
            }
            // Note: BIM ZIPs are already filtered out in validation above
          } catch (error: unknown) {
            console.error(`❌ Failed to process ZIP file ${file.name}:`, error);
            // Add the ZIP file itself as a fallback
            processedFiles.push(file);
          }
        } else {
          processedFiles.push(file);
        }
      }

      // Add files to staging area (avoid duplicates)
      setStagedFiles((prev: File[]) => {
        const newFiles = processedFiles.filter(
          (newFile: File) =>
            !prev.some(
              (existingFile: File) =>
                existingFile.name === newFile.name &&
                existingFile.size === newFile.size,
            ),
        );
        return [...prev, ...newFiles];
      });
    } catch (error: unknown) {
      console.error("Error staging files:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      alert(`Error staging files: ${errorMessage}`);
    } finally {
      if (hasZipFiles) {
        // Brief delay to ensure loading state is visible
        setTimeout(() => {
          setIsExtracting(false);
          setLocalExtractingFileCount(0);
        }, 500);
      }
    }
  };

  // Remove file from staging
  const removeStagedFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Clear all staged files
  const clearStagedFiles = () => {
    setStagedFiles([]);
  };

  // Process all staged files
  const uploadStagedFiles = async () => {
    if (stagedFiles.length === 0) return;

    setIsUploading(true);

    try {
      for (const file of stagedFiles) {
        const fileType = file.name.split(".").pop();

        if (fileType === "geojson" || fileType === "json") {
          const reader = new FileReader();
          reader.onload = async () => {
            if (reader.result && typeof reader.result === "string") {
              const geojson = await convertToGeoJSON(reader.result, fileType);
              onFileUpload({ data: geojson, title: file.name }, fileType);
            }
          };
          reader.readAsText(file);
        } else if (fileType === "zip") {
          await convertToGeoJSON(file, fileType);
        } else if (fileType) {
          try {
            const geojson = await convertToGeoJSON(file, fileType);
            onFileUpload({ data: geojson, title: file.name }, fileType);
          } catch (error: unknown) {
            console.error(`Error processing ${file.name}:`, error);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            alert(`Error processing ${file.name}: ${errorMessage}`);
          }
        }
      }

      // Clear staged files after successful upload
      clearStagedFiles();
    } catch (error: unknown) {
      console.error("Error uploading files:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      alert(`Error uploading files: ${errorMessage}`);
    } finally {
      setIsUploading(false);
      closeModal({ id: "upload-modal" });
    }
  };

  return (
    <div className="space-y-4">
      {isButton ? (
        <Button
          text={text}
          leadingIcon="upload"
          onClick={() => {
            // Programmatically trigger file input click
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = true;
            input.accept = fileTypes?.join(",") || "";
            input.onchange = async (e) => {
              const target = e.target as HTMLInputElement;
              if (target.files) {
                // For button mode, process files immediately instead of staging
                const filesArray = Array.from(target.files);

                try {
                  for (const file of filesArray) {
                    const fileType = file.name.split(".").pop()?.toLowerCase();

                    if (fileType === "geojson" || fileType === "json") {
                      const reader = new FileReader();
                      reader.onload = async () => {
                        if (
                          reader.result &&
                          typeof reader.result === "string"
                        ) {
                          try {
                            const geojson = await convertToGeoJSON(
                              reader.result,
                              fileType,
                            );
                            onFileUpload(
                              { data: geojson, title: file.name },
                              fileType,
                            );
                          } catch (error: unknown) {
                            console.error(
                              `Error processing ${file.name}:`,
                              error,
                            );
                            const errorMessage =
                              error instanceof Error
                                ? error.message
                                : "Unknown error";
                            alert(
                              `Error processing ${file.name}: ${errorMessage}`,
                            );
                          }
                        }
                      };
                      reader.readAsText(file);
                    } else if (fileType === "zip") {
                      try {
                        await convertToGeoJSON(file, fileType);
                      } catch (error: unknown) {
                        console.error(`Error processing ${file.name}:`, error);
                        const errorMessage =
                          error instanceof Error
                            ? error.message
                            : "Unknown error";
                        alert(`Error processing ${file.name}: ${errorMessage}`);
                      }
                    } else if (fileType === "csv") {
                      const reader = new FileReader();
                      reader.onload = async () => {
                        if (
                          reader.result &&
                          typeof reader.result === "string"
                        ) {
                          try {
                            const geojson = await convertToGeoJSON(
                              reader.result,
                              fileType,
                            );
                            onFileUpload(
                              { data: geojson, title: file.name },
                              fileType,
                            );
                          } catch (error: unknown) {
                            console.error(
                              `Error processing ${file.name}:`,
                              error,
                            );
                            const errorMessage =
                              error instanceof Error
                                ? error.message
                                : "Unknown error";
                            alert(
                              `Error processing ${file.name}: ${errorMessage}`,
                            );
                          }
                        }
                      };
                      reader.readAsText(file);
                    } else if (fileType) {
                      try {
                        const geojson = await convertToGeoJSON(file, fileType);
                        onFileUpload(
                          { data: geojson, title: file.name },
                          fileType,
                        );
                      } catch (error: unknown) {
                        console.error(`Error processing ${file.name}:`, error);
                        const errorMessage =
                          error instanceof Error
                            ? error.message
                            : "Unknown error";
                        alert(`Error processing ${file.name}: ${errorMessage}`);
                      }
                    }
                  }
                } catch (error: unknown) {
                  console.error("Error processing files:", error);
                  const errorMessage =
                    error instanceof Error ? error.message : "Unknown error";
                  alert(`Error processing files: ${errorMessage}`);
                }
              }
            };
            input.click();
          }}
          {...props}
        />
      ) : (
        <div
          className={cn("px-4 pt-4 pb-4", stagedFiles.length ? "pb-0" : "pb-4")}
        >
          <FileDrop
            onDrop={handleFileChange}
            showImage={false}
            multiple={true}
            text={
              (isExtracting || parentIsExtracting) && stagedFiles.length === 0
                ? "Extracting files from ZIP..."
                : text
            }
            icon={
              (isExtracting || parentIsExtracting) && stagedFiles.length === 0
                ? "loading"
                : "file/json-28"
            }
            fileTypes={fileTypes}
            fileTypesText={fileTypesText}
            className="min-h-32"
            loading={
              (isExtracting || parentIsExtracting) && stagedFiles.length === 0
            }
            stagedFiles={stagedFiles}
            onRemoveStagedFile={removeStagedFile}
            showStagedFiles={true}
            showSkeletons={
              (isExtracting || parentIsExtracting) && stagedFiles.length === 0
            }
            skeletonCount={
              localExtractingFileCount || parentExtractingFileCount || 2
            } // Show skeleton for each file being processed
          />
        </div>
      )}

      {/* Upload controls - only show when files are staged */}
      {stagedFiles.length > 0 && (
        <div className="flex justify-between items-center p-4 border-t">
          <div className="flex gap-2">
            <Button
              leadingIcon="trash"
              critical
              onClick={clearStagedFiles}
              disabled={isUploading}
            >
              {t("util:discard", {
                val: getLabel(stagedFiles.length),
              })}{" "}
              {stagedFiles.length > 1 ? `(${stagedFiles.length})` : ""}
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              {t("global:totalSize")}: {(() => {
                const totalBytes = stagedFiles.reduce(
                  (sum, file) => sum + file.size,
                  0,
                );
                if (totalBytes === 0) return "0 Bytes";
                const k = 1024;
                const sizes = ["Bytes", "KB", "MB", "GB"];
                const i = Math.floor(Math.log(totalBytes) / Math.log(k));
                return (
                  Number.parseFloat((totalBytes / k ** i).toFixed(2)) +
                  " " +
                  sizes[i]
                );
              })()}
            </div>
            <Button
              variant="primary"
              text={`Upload ${stagedFiles.length} file${stagedFiles.length !== 1 ? "s" : ""}`}
              size="lg"
              onClick={uploadStagedFiles}
              loading={isUploading}
              disabled={isUploading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface UploadMapDataProps {
  handleBimAssetsComplete: (assets: SavedAsset[]) => void;
  handleUploadGeoJSON: (data?: any, fileType?: string, index?: number) => void;
  formik?: any;
  initialFiles?: File[];
  preCategorizedFiles?: {
    gisFiles: File[];
    bimFiles: File[];
    unknownFiles: File[];
    zipFileNames?: string[];
  };
}

const UploadMapData = ({
  handleBimAssetsComplete,
  handleUploadGeoJSON,
  formik,
  initialFiles = [],
  preCategorizedFiles,
}: UploadMapDataProps) => {
  const { t } = useTranslation();
  const [gisFiles, setGisFiles] = useState<File[]>([]);
  const [bimFiles, setBimFiles] = useState<File[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractingFileCount, setExtractingFileCount] = useState(0);

  // Auto-select tab based on file types
  const getInitialTab = (gisCount: number, bimCount: number) => {
    if (bimCount > 0 && gisCount === 0) {
      return "BIM";
    }
    // Default to GIS (even if mixed files or no files)
    return "GIS";
  };

  const [activeTab, setActiveTab] = useState<string | number>("GIS");
  const [zipFileNames, setZipFileNames] = useState<string[]>([]);

  // Initialize files and tab when component mounts
  useEffect(() => {
    if (isInitialized) return; // Prevent re-initialization

    if (preCategorizedFiles) {
      // Files are already categorized and extracted - use them directly
      console.log("📋 Using pre-categorized files");
      setGisFiles(preCategorizedFiles.gisFiles);
      setBimFiles(preCategorizedFiles.bimFiles);

      // Set initial tab based on file counts
      const initialTab = getInitialTab(
        preCategorizedFiles.gisFiles.length,
        preCategorizedFiles.bimFiles.length,
      );
      setActiveTab(initialTab);
      setIsInitialized(true);
    } else if (initialFiles.length > 0) {
      // Quick pre-analysis to set initial tab faster
      const hasPotentialBimFiles = initialFiles.some((file) => {
        const fileName = file.name.toLowerCase();
        return (
          fileName.endsWith(".ifc") ||
          fileName.endsWith(".dwg") ||
          fileName.endsWith(".dxf") ||
          fileName.endsWith(".rvt") ||
          fileName.endsWith(".rfa") ||
          fileName.endsWith(".skp") ||
          fileName.endsWith(".3ds") ||
          fileName.endsWith(".obj") ||
          fileName.endsWith(".fbx") ||
          fileName.endsWith(".dae") ||
          (fileName.endsWith(".zip") &&
            (fileName.includes("bim") ||
              fileName.includes("ifc") ||
              fileName.includes("model")))
        );
      });

      // Set initial tab based on quick analysis
      if (hasPotentialBimFiles) {
        setActiveTab("BIM");
      }

      // Check if there are ZIP files to show extraction loading
      const hasZipFiles = initialFiles.some((file) =>
        file.name.toLowerCase().endsWith(".zip"),
      );

      // Categorize files here with extraction feedback if needed
      const categorizeInitialFiles = async () => {
        if (hasZipFiles) {
          setIsExtracting(true);
          setExtractingFileCount(initialFiles.length);
        }

        console.log(
          `🚀 Starting file categorization for ${initialFiles.length} files`,
        );

        try {
          const {
            gisFiles: categorizedGisFiles,
            bimFiles: categorizedBimFiles,
            zipFileNames: extractedZipNames,
          } = await categorizeFiles(initialFiles, (count) => {
            // Update skeleton count as soon as we know how many files will be extracted
            setExtractingFileCount(count);
          });

          console.log(`📊 File categorization complete:
            - GIS files: ${categorizedGisFiles.length}
            - BIM files: ${categorizedBimFiles.length}
            - ZIP file names: ${extractedZipNames.join(", ")}`);

          setGisFiles(categorizedGisFiles);
          setBimFiles(categorizedBimFiles);

          // Store ZIP file names for BIM uploader
          if (extractedZipNames.length > 0) {
            setZipFileNames(extractedZipNames);
          }

          const initialTab = getInitialTab(
            categorizedGisFiles.length,
            categorizedBimFiles.length,
          );
          setActiveTab(initialTab);
          setIsInitialized(true);
        } catch (error: unknown) {
          console.error("Error categorizing files:", error);
          // Default to GIS tab if categorization fails
          setActiveTab("GIS");
          setIsInitialized(true);
        } finally {
          if (hasZipFiles) {
            // Brief delay to ensure loading state is visible
            setTimeout(() => {
              setIsExtracting(false);
              setExtractingFileCount(0);
            }, 500);
          }
        }
      };

      categorizeInitialFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount, but we need the props from closure
  return (
    <div>
      <div className="border-b px-5">
        <Tabs
          classNames={{
            wrapper: "h-14",
          }}
          active={activeTab}
          setActive={(newTab) => {
            console.log("🔄 Tab switch requested:", activeTab, "→", newTab);
            setActiveTab(newTab);
          }}
          tabs={[
            {
              text: "GIS",
              tab: "GIS",
            },
            {
              text: "BIM",
              tab: "BIM",
            },
          ]}
        />
      </div>
      {activeTab === "GIS" ? (
        <GeoJSONUploader
          text={`${t("util:upload", {
            val: `${t("global:geodata")}`,
          })} (WGS84 / UTM zone 32 / EPSG::25832)`}
          onFileUpload={handleUploadGeoJSON}
          fileTypes={["geojson", "json", "csv", "zip"]}
          fileTypesText="GeoJSON, JSON, CSV, Shapefiles-zip"
          initialStagedFiles={gisFiles}
          isExtracting={isExtracting}
          parentExtractingFileCount={extractingFileCount}
        />
      ) : (
        <BimUploader
          onAssetsComplete={handleBimAssetsComplete}
          formik={formik}
          initialStagedFiles={bimFiles}
          zipFileName={
            preCategorizedFiles?.zipFileNames?.[0] || zipFileNames[0]
          } // Pass first ZIP name from either source
          isExtracting={isExtracting}
          parentExtractingFileCount={extractingFileCount}
        />
      )}
    </div>
  );
};

export const UploadFileDrop = ({
  onDrop,
  fileTypes,
  fileTypesText,
  handleBimAssetsComplete,
  handleUploadGeoJSON,
  formik,
}: {
  onDrop?: (files: File[] | FileList) => void;
  fileTypes: string[];
  fileTypesText: string;
  handleBimAssetsComplete: (assets: SavedAsset[]) => void;
  handleUploadGeoJSON: (data?: any, fileType?: string, index?: number) => void;
  formik: any;
}) => {
  const { openModal } = useModals();

  const handleFileDrop = (files: File[] | FileList) => {
    const filesArray = Array.from(files);

    // Call the original onDrop if it's provided
    if (onDrop) {
      onDrop(files);
    }

    // Open modal immediately with raw files - extraction will happen inside
    openModal(
      <UploadMapData
        handleBimAssetsComplete={handleBimAssetsComplete}
        handleUploadGeoJSON={handleUploadGeoJSON}
        formik={formik}
        initialFiles={filesArray}
      />,
      {
        id: "upload-modal",
        zIndex: 10000,
        size: "lg",
      },
    );
  };

  return (
    <FileDrop
      onDrop={handleFileDrop}
      fileTypes={fileTypes}
      fileTypesText={fileTypesText}
      loading={false}
      onClick={() => {
        openModal(
          <UploadMapData
            handleBimAssetsComplete={handleBimAssetsComplete}
            handleUploadGeoJSON={handleUploadGeoJSON}
            formik={formik}
          />,
          {
            id: "upload-modal",
            zIndex: 10000,
            size: "lg",
          },
        );
      }}
    />
  );
};
